import { toast, errorToast } from "@/v2-ui/toast"
import { ConfirmButton } from "@/v2-backoffice-client/components/button"
import { useUserMigrateManyMutation } from "@/v2-backoffice-client/user/migration/many/user.migration.many.mutation"
import type { ActivationRunOutcome } from "@/v2-backoffice-client/tenant/migration/tenantMigration.constants"

export type MigrationCenterScopeSendTeam = {
  tenantId: number
  /** The user ids the send targets — exactly the rows the operator sees
   *  in this scope after filters and exclusions (already reduced to the
   *  wave-sendable statuses). */
  userIds: number[]
}

export type MigrationCenterScopeSendButtonProps = {
  teams: MigrationCenterScopeSendTeam[]
  /** Names the send in the confirm copy and the completion toast. */
  scopeLabel: string
  children?: string
  onOutcome(userId: number, outcome: ActivationRunOutcome): void
  /** Any send changes board state — the host refetches the boards. */
  onChanged?(): void
}

/**
 * One send trigger of the Migration Center (team card, customer group, or
 * the footer's send-everything): WHAT YOU SEE IS WHAT SENDS. The send
 * targets exactly the visible rows of its scope — after the app filter and
 * per-member exclusions — one pinned call per team, so multi-team members
 * migrate under the team they're listed in. A member reached by an earlier
 * team of the same run holds a fresh pending link by the time the next
 * team sends, and the wave's skip-pending rule leaves them alone.
 */
function MigrationCenterScopeSendButton(
  props: MigrationCenterScopeSendButtonProps,
) {
  const { teams, scopeLabel, children, onOutcome, onChanged } = props

  const [migrateUsers, { loading, error }] = useUserMigrateManyMutation()
  errorToast(error, { title: "Failed to send Fusion activations" })

  const sendable = teams.reduce((sum, t) => sum + t.userIds.length, 0)

  async function run() {
    let sent = 0
    let skipped = 0
    let failed = 0
    for (const team of teams) {
      if (team.userIds.length === 0) continue
      const result = await migrateUsers({
        variables: { userIds: team.userIds, tenantId: team.tenantId },
      })
      if (result.errors?.length) {
        failed += team.userIds.length
        continue
      }
      for (const outcome of result.data?.migrateUsersToEntra ?? []) {
        onOutcome(outcome.UserId, {
          outcome: outcome.Outcome,
          error: outcome.Error,
        })
        if (outcome.Outcome === "SENT" || outcome.Outcome === "RENEWED") sent++
        else if (outcome.Error) failed++
        else skipped++
      }
    }
    toast({
      title: `${scopeLabel}: ${sent} activation(s) sent`,
      description:
        [
          skipped ? `${skipped} skipped` : null,
          failed ? `${failed} failed — check the rows` : null,
        ]
          .filter(Boolean)
          .join(" · ") || undefined,
      variant: failed ? "destructive" : undefined,
    })
    onChanged?.()
  }

  return (
    <ConfirmButton
      variant="outline"
      size="tiny"
      iconSrc="paper-plane"
      content={`Send activations to the listed members of ${scopeLabel}`}
      confirmContent={`Send ${sendable} activation(s) across ${teams.filter((t) => t.userIds.length > 0).length} team(s)? Only the members currently listed are sent.`}
      isLoading={loading}
      disabled={!sendable}
      onConfirm={() => void run()}
    >
      {children ?? `Send ${sendable}`}
    </ConfirmButton>
  )
}

export default MigrationCenterScopeSendButton
